import {
  OWNER_TRANSITION_DRAIN_FAILURE_KEY,
  OWNER_TRANSITION_KEY,
} from './owner-transition-keys';

const OWNER_RUNTIME_LOCK = 'watch-him-work-owner-runtime-v1';
const OWNER_TRANSITION_COORDINATOR_LOCK =
  'watch-him-work-owner-transition-coordinator-v1';
const TRANSITION_TTL_MS = 30_000;
const TRANSITION_REFRESH_MS = 10_000;

type OwnerTransitionMarker = {
  id: string;
  targetOwnerId: string;
  expiresAt: number;
  phase?: 'draining' | 'exclusive';
};

/** Each drain learns which marker it answers (retry keys are per marker id). */
type OwnerTransitionDrain = (marker: { id: string }) => Promise<void>;

const transitionDrains = new Set<OwnerTransitionDrain>();
const localBarrierObservers = new Set<() => void>();
let activeDrain: { markerId: string; promise: Promise<void>; failed: boolean } | null = null;
// Marker ids this page's own exclusive transition wrote (same-page hand-off).
const localTransitionIds = new Set<string>();

/**
 * Owner-scoped in-memory work that outlives the owner runtime (voice-capture
 * custody, account-change recording plan rev 4.1, sections 5.3 and 6). The
 * shared runtime lease consults it after its abort: `waiting` keeps the lock
 * until the work settles; `blocked` keeps it too, but a draining marker is
 * answered through the verified, marker-specific failure hand-off.
 */
export type OwnerRuntimeWorkState = 'free' | 'waiting' | 'blocked';
export interface OwnerRuntimeWorkSource {
  state: (ownerId: string) => OwnerRuntimeWorkState;
  subscribe: (listener: () => void) => () => void;
  retryForMarker: (markerId: string) => Promise<unknown>;
}
let ownerWorkSource: OwnerRuntimeWorkSource | null = null;
// Re-check at least this often: a marker can expire without any event.
const OWNER_WORK_RECHECK_MS = 1_000;

export function registerOwnerRuntimeWorkSource(source: OwnerRuntimeWorkSource): void {
  ownerWorkSource = source;
}

function ownerWorkState(ownerId: string): OwnerRuntimeWorkState {
  try {
    return ownerWorkSource?.state(ownerId) ?? 'free';
  } catch {
    // Fail closed: unreadable owner work is never treated as settled.
    return 'blocked';
  }
}

function readMarker(): OwnerTransitionMarker | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(OWNER_TRANSITION_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<OwnerTransitionMarker>;
    if (
      typeof value.id !== 'string' || value.id === '' ||
      typeof value.targetOwnerId !== 'string' || value.targetOwnerId === '' ||
      typeof value.expiresAt !== 'number' || !Number.isFinite(value.expiresAt) ||
      (value.phase !== undefined && value.phase !== 'draining' && value.phase !== 'exclusive') ||
      value.expiresAt > Date.now() + TRANSITION_TTL_MS
    ) return null;
    return value as OwnerTransitionMarker;
  } catch {
    // The Web Lock is the authoritative exclusion boundary. A corrupt marker
    // must not strand every tab behind an infinite presentation-layer barrier.
    return null;
  }
}

export function isOwnerTransitionBarrierActive(): boolean {
  const marker = readMarker();
  return marker !== null && marker.expiresAt > Date.now();
}

/**
 * Presentation closes only after the draining callbacks settle. Keeping the
 * owner runtime mounted during that phase is what lets MediaRecorder deliver
 * its final data event and commit the resulting Blob before the shared lock is
 * released. Legacy markers have no phase and remain conservatively exclusive.
 */
export function isOwnerTransitionPresentationBarrierActive(): boolean {
  const marker = readMarker();
  return Boolean(
    marker &&
    marker.expiresAt > Date.now() &&
    marker.phase !== 'draining',
  );
}

/** Writes are allowed while old runtimes drain, then blocked under exclusivity. */
export function isOwnerTransitionWriteBarrierActive(): boolean {
  const marker = readMarker();
  return Boolean(
    marker &&
    marker.expiresAt > Date.now() &&
    marker.phase !== 'draining',
  );
}

function requestOwnerTransitionDrain(marker: OwnerTransitionMarker): Promise<void> {
  if (activeDrain?.markerId === marker.id) return activeDrain.promise;
  const callbacks = [...transitionDrains];
  const record = { markerId: marker.id, promise: Promise.resolve(), failed: false };
  record.promise = Promise.all(callbacks.map((drain) => drain({ id: marker.id })))
    .then(() => undefined)
    .catch((error) => {
      record.failed = true;
      try {
        localStorage.setItem(OWNER_TRANSITION_DRAIN_FAILURE_KEY, marker.id);
      } catch {
        // The exclusive transition still checks its in-process promise.
      }
      throw error;
    });
  activeDrain = record;
  return record.promise;
}

/** Register owner-scoped in-memory work that must become durable before release. */
export function registerOwnerTransitionDrain(drain: OwnerTransitionDrain): () => void {
  transitionDrains.add(drain);
  const marker = readMarker();
  if (marker?.phase === 'draining' && marker.expiresAt > Date.now()) {
    void requestOwnerTransitionDrain(marker).catch(() => {});
  }
  return () => transitionDrains.delete(drain);
}

export function subscribeOwnerTransitionBarrier(
  listener: (active: boolean) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let expiryTimer: number | null = null;
  let pendingDrainMarkerId: string | null = null;
  let disposed = false;

  const clearExpiryTimer = () => {
    if (expiryTimer === null) return;
    window.clearTimeout(expiryTimer);
    expiryTimer = null;
  };
  const observe = (notify: boolean) => {
    clearExpiryTimer();
    const marker = readMarker();
    const active = marker !== null && marker.expiresAt > Date.now();
    if (active && marker?.phase === 'draining') {
      const drain = requestOwnerTransitionDrain(marker);
      if (notify && pendingDrainMarkerId !== marker.id) {
        pendingDrainMarkerId = marker.id;
        void drain.finally(() => {
          if (disposed) return;
          if (pendingDrainMarkerId === marker.id) pendingDrainMarkerId = null;
          const current = readMarker();
          if (current?.id === marker.id && current.expiresAt > Date.now()) {
            listener(true);
          }
        }).catch(() => {});
      }
    }
    if (active && marker) {
      expiryTimer = window.setTimeout(
        () => observe(true),
        Math.max(0, marker.expiresAt - Date.now()),
      );
    }
    if (notify && marker?.phase !== 'draining') listener(active);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === OWNER_TRANSITION_KEY || event.key === null) observe(true);
  };
  const localObserver = () => observe(true);
  window.addEventListener('storage', onStorage);
  localBarrierObservers.add(localObserver);
  observe(false);
  return () => {
    disposed = true;
    clearExpiryTimer();
    window.removeEventListener('storage', onStorage);
    localBarrierObservers.delete(localObserver);
  };
}

function writeMarker(marker: OwnerTransitionMarker): void {
  localStorage.setItem(OWNER_TRANSITION_KEY, JSON.stringify(marker));
  const confirmed = readMarker();
  if (confirmed?.id !== marker.id) throw new Error('owner-transition-marker-failed');
  for (const observe of [...localBarrierObservers]) observe();
  if (marker.phase === 'draining') {
    void requestOwnerTransitionDrain(marker).catch(() => {});
  }
}

function clearMarker(id: string): void {
  const current = readMarker();
  if (current?.id === id) {
    localStorage.removeItem(OWNER_TRANSITION_KEY);
    for (const observe of [...localBarrierObservers]) observe();
  }
  if (localStorage.getItem(OWNER_TRANSITION_DRAIN_FAILURE_KEY) === id) {
    localStorage.removeItem(OWNER_TRANSITION_DRAIN_FAILURE_KEY);
  }
  if (activeDrain?.markerId === id) activeDrain = null;
}

/**
 * Hold the browser-wide exclusive transition lock around fetch, validation,
 * and installation. Every open owner runtime holds the same lock in shared
 * mode, so replacement waits for all tabs to close their owner-bearing work.
 */
export async function runExclusiveOwnerTransition<T>(
  targetOwnerId: string,
  task: () => Promise<T>,
): Promise<T> {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return task();
  }
  if (!navigator.locks) throw new Error('owner-transition-lock-unavailable');
  // A separate browser-wide coordinator lock serializes the COMPLETE marker
  // protocol. Without it, two sign-in/adoption contenders can overwrite each
  // other's marker while the first waits for owner runtimes to drain. A remote
  // tab can then report failure against the second id and the first contender
  // can miss that failure. The runtime lock remains a distinct inner lock:
  // owner runtimes hold it shared for their lifetime, while exactly one
  // coordinated replacement acquires it exclusively after publishing the
  // draining marker.
  return navigator.locks.request(
    OWNER_TRANSITION_COORDINATOR_LOCK,
    { mode: 'exclusive' },
    async () => {
      const id = crypto.randomUUID();
      localTransitionIds.add(id);
      let phase: OwnerTransitionMarker['phase'] = 'draining';
      const refresh = () => writeMarker({
        id,
        targetOwnerId,
        expiresAt: Date.now() + TRANSITION_TTL_MS,
        phase,
      });
      refresh();
      const timer = window.setInterval(() => {
        try {
          refresh();
        } catch {
          // The Web Locks still provide exclusion if storage becomes unavailable.
        }
      }, TRANSITION_REFRESH_MS);
      try {
        return await navigator.locks.request(
          OWNER_RUNTIME_LOCK,
          { mode: 'exclusive' },
          async () => {
            const draining = activeDrain?.markerId === id
              ? activeDrain.promise
              : Promise.resolve();
            // A rejected drain in this page refuses the replacement exactly as
            // a failure reported by another page does.
            await draining.catch(() => {
              throw new Error('owner-transition-drain-failed');
            });
            if (localStorage.getItem(OWNER_TRANSITION_DRAIN_FAILURE_KEY) === id) {
              throw new Error('owner-transition-drain-failed');
            }
            phase = 'exclusive';
            refresh();
            return task();
          },
        );
      } finally {
        window.clearInterval(timer);
        clearMarker(id);
        localTransitionIds.delete(id);
      }
    },
  );
}

/** Resolves on the next owner-work, marker or storage change (or a recheck). */
function nextOwnerWorkChange(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      unsubscribe();
      window.removeEventListener('storage', finish);
      localBarrierObservers.delete(finish);
      window.clearTimeout(timer);
      resolve();
    };
    const unsubscribe = ownerWorkSource?.subscribe(finish) ?? (() => undefined);
    window.addEventListener('storage', finish);
    localBarrierObservers.add(finish);
    const timer = window.setTimeout(finish, OWNER_WORK_RECHECK_MS);
  });
}

function markerStillDraining(marker: OwnerTransitionMarker): boolean {
  const current = readMarker();
  return Boolean(
    current &&
    current.id === marker.id &&
    current.phase === 'draining' &&
    current.expiresAt > Date.now(),
  );
}

/**
 * Plan rev 4.1, section 6.3. Proof that marker M's replacement will be
 * refused once this lock is released: this page's own transition with a
 * rejected in-process drain, or (any page) the failure key written for M and
 * read back, with M still the current draining marker. Anything unverifiable
 * keeps the lock (fail closed).
 */
function verifyFailureHandoff(marker: OwnerTransitionMarker): boolean {
  if (!markerStillDraining(marker)) return false;
  if (
    localTransitionIds.has(marker.id) &&
    activeDrain?.markerId === marker.id &&
    activeDrain.failed
  ) return markerStillDraining(marker);
  try {
    localStorage.setItem(OWNER_TRANSITION_DRAIN_FAILURE_KEY, marker.id);
    if (localStorage.getItem(OWNER_TRANSITION_DRAIN_FAILURE_KEY) !== marker.id) return false;
  } catch {
    return false;
  }
  return markerStillDraining(marker);
}

/**
 * Whether an exclusive request for the runtime lock is already in the lock
 * queue. Publishing a marker does not prove that its writer's exclusive
 * request has reached the queue: the storage event and the lock request travel
 * separately across pages. Only the coordinator holder, which wrote the
 * current marker, requests this lock exclusively. Unknown (no `query`, or it
 * throws) is `false`, so the lease keeps the lock (fail closed).
 */
async function runtimeExclusiveRequestQueued(): Promise<boolean> {
  try {
    if (typeof navigator.locks.query !== 'function') return false;
    const snapshot = await navigator.locks.query();
    return Boolean(snapshot.pending?.some(
      (request) => request.name === OWNER_RUNTIME_LOCK && request.mode === 'exclusive',
    ));
  } catch {
    return false;
  }
}

/** Per-lease memory: the one retry per marker, and markers already handed off. */
type HandoffMemory = { retried: Set<string>; handedOff: Set<string> };

/**
 * Plan rev 4.1, section 6.2: after the lease's abort, keep the shared lock
 * while the owner's work is `waiting`; when it is `blocked`, answer each valid
 * draining marker: its retry once, then the verified hand-off once. The
 * hand-off releases only after the marker's exclusive request is seen in the
 * lock queue, with the marker and failure proof verified after that check;
 * then, in the same task, it asks again, so that earlier exclusive request
 * runs first, reads the failure and aborts. Until the request is seen, or
 * when anything is unverifiable, the lock is kept and the check repeats on the
 * next change or recheck (no release, so no release and re-acquire loop).
 */
async function holdWhileOwnerWorkUnsettled(
  ownerId: string,
  memory: HandoffMemory,
): Promise<void> {
  // One task: a teardown in the same commit claims its capture first.
  await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
  for (;;) {
    const state = ownerWorkState(ownerId);
    if (state === 'free') return;
    if (state === 'blocked') {
      const marker = readMarker();
      if (
        marker &&
        marker.phase === 'draining' &&
        marker.expiresAt > Date.now() &&
        !memory.handedOff.has(marker.id)
      ) {
        if (!memory.retried.has(marker.id)) {
          memory.retried.add(marker.id);
          await ownerWorkSource?.retryForMarker(marker.id).catch(() => undefined);
          if (ownerWorkState(ownerId) !== 'blocked') continue;
        }
        if (
          markerStillDraining(marker) &&
          await runtimeExclusiveRequestQueued() &&
          ownerWorkState(ownerId) === 'blocked' &&
          verifyFailureHandoff(marker)
        ) {
          memory.handedOff.add(marker.id);
          void navigator.locks.request(
            OWNER_RUNTIME_LOCK,
            { mode: 'shared' },
            () => holdWhileOwnerWorkUnsettled(ownerId, memory),
          ).catch(() => undefined);
          return;
        }
      }
    }
    await nextOwnerWorkChange();
  }
}

/** Hold one shared lock for the complete lifetime of an open owner runtime. */
export async function holdSharedOwnerRuntimeLock(
  signal: AbortSignal,
  onAcquired: () => void | boolean | Promise<void | boolean>,
  ownerId?: string,
): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    throw new Error('owner-runtime-lock-unavailable');
  }
  await navigator.locks.request(
    OWNER_RUNTIME_LOCK,
    { mode: 'shared', signal },
    async () => {
      if (signal.aborted || isOwnerTransitionBarrierActive()) return;
      const accepted = await onAcquired();
      if (accepted === false || signal.aborted) return;
      await new Promise<void>((resolve) => {
        if (signal.aborted) {
          resolve();
          return;
        }
        signal.addEventListener('abort', () => resolve(), { once: true });
      });
      const marker = readMarker();
      if (marker?.phase === 'draining' && marker.expiresAt > Date.now()) {
        await requestOwnerTransitionDrain(marker).catch(() => {});
      }
      // The owner's captures from this page are released only once settled,
      // or through the verified hand-off (plan rev 4.1, section 6).
      if (ownerId) {
        await holdWhileOwnerWorkUnsettled(ownerId, { retried: new Set(), handedOff: new Set() });
      }
    },
  );
}

export { OWNER_TRANSITION_KEY } from './owner-transition-keys';
